import { supabase } from '@/lib/supabase/client';
import type { Tables } from '@/lib/supabase/types';
import { queryOptions } from '@tanstack/react-query';

export type ProjectMemberWithProfile = {
  user_id: string;
  role: string;
  added_at: string | null;
  user: Pick<Tables<'profiles'>, 'id' | 'display_name' | 'avatar_url' | 'email'> | null;
};

export type ProjectRow = Tables<'projects'>;

export const projectsKeys = {
  all: ['projects'] as const,
  workspace: (workspaceId: string) => ['projects', 'workspace', workspaceId] as const,
  bySlug: (workspaceId: string, slug: string) =>
    ['projects', 'workspace', workspaceId, 'slug', slug] as const,
  byId: (id: string) => ['projects', 'id', id] as const,
};

export function listProjectsByWorkspaceQuery(workspaceId: string) {
  return queryOptions({
    queryKey: projectsKeys.workspace(workspaceId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('workspace_id', workspaceId)
        .is('archived_at', null)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ProjectRow[];
    },
    staleTime: 60_000,
  });
}

export function listArchivedProjectsByWorkspaceQuery(workspaceId: string) {
  return queryOptions({
    queryKey: [...projectsKeys.workspace(workspaceId), 'archived'] as const,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('workspace_id', workspaceId)
        .not('archived_at', 'is', null)
        .order('archived_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ProjectRow[];
    },
    staleTime: 30_000,
  });
}

export function getProjectByIdQuery(id: string) {
  return queryOptions({
    queryKey: projectsKeys.byId(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return data as ProjectRow | null;
    },
    staleTime: 60_000,
  });
}

export function getProjectBySlugQuery(workspaceId: string, slug: string) {
  return queryOptions({
    queryKey: projectsKeys.bySlug(workspaceId, slug),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('slug', slug)
        .maybeSingle();
      if (error) throw error;
      return data as ProjectRow | null;
    },
    staleTime: 60_000,
  });
}

export function listProjectMembersQuery(projectId: string) {
  return queryOptions({
    queryKey: ['project_members', projectId],
    queryFn: async () => {
      // project_members.user_id → profiles.id (FK unico, no requiere disambiguar)
      const { data, error } = await supabase
        .from('project_members')
        .select(
          'user_id, role, added_at, user:profiles!project_members_user_id_fkey(id, display_name, avatar_url, email)',
        )
        .eq('project_id', projectId)
        .order('added_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as ProjectMemberWithProfile[];
    },
    staleTime: 60_000,
  });
}
